/* eslint-disable max-len */
import React, { useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { IconButton } from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';

import { useStore } from 'utils/hooks/store';
import { DocumentAccessLevel } from 'utils/api/modules/entities/entities.types';

import { useKeepOnPage } from 'containers/Tiptap/hooks/use-keep-on-page';

import { RiskMatrixConfigDialog } from './components/risk-matrix-config-dialog';
import { RiskMatrixSummary } from './components/risk-matrix-summary';
import { ConfigurableRiskMatrixTable } from './components/configurable-risk-matrix-table';
import { RiskMatrixConfigData, RiskMatrixNodeComponentProps } from './types';
import { StyledRiskMatrixFullDisplay } from './styles';

export const RiskMatrixNodeComponent: React.FC<RiskMatrixNodeComponentProps> = ({
  node: {
    attrs,
    attrs: {
      rows,
      columns,
      impactLabels,
      likelihoodLabels,
      selectedImpact,
      selectedLikelihood,
      selectedRisk,
      editable,
      showSummary,
    },
  },
  getPos,
  editor,
}) => {
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [configData, setConfigData] = useState<RiskMatrixConfigData>({
    rows,
    columns,
    impactLabels: [...impactLabels],
    likelihoodLabels: [...likelihoodLabels],
    showSummary,
  });

  const { documentsDetails } = useStore();
  // viewOnlyEditor stays editable:true (TipTap Pro extensions blank the doc otherwise),
  // so we also gate on the document access level here.
  const isNodeEditable = editor.isEditable && documentsDetails.accessLevel !== DocumentAccessLevel.View;

  // Push the matrix to the next page when it would straddle a page break (ENG-11115).
  const wrapperRef = useKeepOnPage({ editor, getPos });

  const updateNodeAttributes = (newAttrs: Partial<typeof attrs>): void => {
    if (typeof getPos === 'function') {
      const pos = getPos();
      if (pos === undefined) return;
      editor.chain().focus().setNodeSelection(pos).updateAttributes('riskMatrix', newAttrs).run();
    }
  };

  const handleConfigSave = (): void => {
    updateNodeAttributes({
      rows: configData.rows,
      columns: configData.columns,
      impactLabels: configData.impactLabels,
      likelihoodLabels: configData.likelihoodLabels,
      showSummary: configData.showSummary,
    });
    setIsConfigDialogOpen(false);
  };

  const handleConfigCancel = (): void => {
    setConfigData({
      rows,
      columns,
      impactLabels: [...impactLabels],
      likelihoodLabels: [...likelihoodLabels],
      showSummary,
    });
    setIsConfigDialogOpen(false);
  };

  const handleRiskUpdate = (impact: number, likelihood: number): void => {
    const risk = impact * likelihood;
    updateNodeAttributes({
      selectedImpact: impact,
      selectedLikelihood: likelihood,
      selectedRisk: risk,
    });
  };

  const handleConfigDataChange = (newConfig: RiskMatrixConfigData): void => {
    setConfigData(newConfig);
  };

  return (
    <NodeViewWrapper ref={wrapperRef}>
      <StyledRiskMatrixFullDisplay>
        <ConfigurableRiskMatrixTable
          selected={{ impact: selectedImpact, likelihood: selectedLikelihood, risk: selectedRisk }}
          onChange={({ impact, likelihood }) => handleRiskUpdate(impact, likelihood)}
          matrixRows={rows}
          matrixColumns={columns}
          impactLabels={impactLabels}
          likelihoodLabels={likelihoodLabels}
          readOnly={!isNodeEditable}
        />

        {editable && isNodeEditable && (
          <IconButton size="small" onClick={() => setIsConfigDialogOpen(true)}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        )}
      </StyledRiskMatrixFullDisplay>

      {showSummary && (
        <RiskMatrixSummary
          selectedImpact={selectedImpact}
          selectedLikelihood={selectedLikelihood}
          selectedRisk={selectedRisk}
          matrixRows={rows}
          matrixColumns={columns}
        />
      )}

      <RiskMatrixConfigDialog
        open={isConfigDialogOpen}
        configData={configData}
        onConfigDataChange={handleConfigDataChange}
        onSave={handleConfigSave}
        onCancel={handleConfigCancel}
      />
    </NodeViewWrapper>
  );
};
